import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { format } from "date-fns";
import {
  useGetEventStatsQuery,
  EventStatItem,
} from "../../../entities/admin-events";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import { renderCaliberName } from "../../../shared/lib/caliber";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import PaymentIcon from "@mui/icons-material/Payment";

interface EventStatsModalProps {
  open: boolean;
  onClose: () => void;
  eventId: number;
  eventTitle: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stats-tabpanel-${index}`}
      aria-labelledby={`stats-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EventStatsModal: React.FC<EventStatsModalProps> = ({
  open,
  onClose,
  eventId,
  eventTitle,
}) => {
  const [tabValue, setTabValue] = React.useState(0);

  const {
    data: statsData,
    isLoading,
    error,
  } = useGetEventStatsQuery(eventId, {
    skip: !open,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (item: EventStatItem) => {
    if (item.paymentAt) return "success";
    if (item.participantAt) return "primary";
    if (item.applicationAt) return "info";
    return "default";
  };

  const getStatusText = (item: EventStatItem) => {
    if (item.paymentAt) return "Оплачено";
    if (item.participantAt) return "Участвовал";
    if (item.applicationAt) return "Подал заявку";
    return "Неизвестно";
  };

  const getStatusDate = (item: EventStatItem) => {
    if (item.paymentAt) return item.paymentAt;
    if (item.participantAt) return item.participantAt;
    if (item.applicationAt) return item.applicationAt;
    return null;
  };

  const getUsersByStatus = (status: "applied" | "participated" | "paid") => {
    const items = statsData?.items || [];
    if (!items.length) return [];

    switch (status) {
      case "applied":
        return items.filter((item) => item.applicationAt);
      case "participated":
        return items.filter((item) => item.participantAt);
      case "paid":
        return items.filter((item) => item.paymentAt);
      default:
        return [];
    }
  };

  // Подсчитываем статистику
  const totalApplications =
    statsData?.items?.filter((item) => item.applicationAt)?.length || 0;
  const totalParticipants =
    statsData?.items?.filter((item) => item.participantAt)?.length || 0;
  const totalPaid =
    statsData?.items?.filter((item) => item.paymentAt)?.length || 0;

  const renderUserTable = (items: EventStatItem[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Пользователь</TableCell>
            <TableCell>Телефон</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Дата</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.user.id}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={item.user.avatarUrl}
                    alt={item.user.firstName}
                    sx={{ width: 32, height: 32 }}
                  >
                    {item.user.firstName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {item.user.firstName} {item.user.lastName}
                    </Typography>
                    {item.user.caliber && (
                      <Typography variant="caption" color="textSecondary">
                        {renderCaliberName(item.user.caliber)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>{item.user.phoneNumber}</TableCell>
              <TableCell>
                <Chip
                  label={
                    item.user.role === "resident"
                      ? "Резидент"
                      : item.user.role === "guest"
                      ? "Гость"
                      : "Пользователь"
                  }
                  color={
                    item.user.role === "resident"
                      ? "primary"
                      : item.user.role === "guest"
                      ? "secondary"
                      : "default"
                  }
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusText(item)}
                  color={getStatusColor(item)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {getStatusDate(item) &&
                  format(new Date(getStatusDate(item)!), "dd.MM.yyyy HH:mm")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <LoadingIndicator />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography color="error">Ошибка загрузки статистики</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Статистика события: {eventTitle}</DialogTitle>
      <DialogContent>
        {statsData?.items && (
          <>
            {/* Общая статистика */}
            <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon color="info" />
                      <Box>
                        <Typography variant="h4">
                          {totalApplications}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Заявок подано
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <GroupIcon color="primary" />
                      <Box>
                        <Typography variant="h4">
                          {totalParticipants}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Участников
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PaymentIcon color="success" />
                      <Box>
                        <Typography variant="h4">{totalPaid}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Оплачено
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Вкладки с пользователями */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label={`Заявки (${totalApplications})`} />
                <Tab label={`Участники (${totalParticipants})`} />
                <Tab label={`Оплачено (${totalPaid})`} />
                <Tab
                  label={`Все пользователи (${statsData.items?.length || 0})`}
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderUserTable(getUsersByStatus("applied"))}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderUserTable(getUsersByStatus("participated"))}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderUserTable(getUsersByStatus("paid"))}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderUserTable(statsData.items || [])}
            </TabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventStatsModal;
